import React, { useState, useEffect } from 'react';
import { Shield, Play, X, Loader2, Tv, CheckCircle2, AlertTriangle } from 'lucide-react';
import API_BASE_URL from '../../config';

const StreakShield = ({ gymcode, onShieldActivated }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAdModal, setShowAdModal] = useState(false);
  const [adSessionId, setAdSessionId] = useState(null);
  const [countdown, setCountdown] = useState(10);
  const [adCompleteStep, setAdCompleteStep] = useState('watching'); // 'watching', 'claiming', 'success', 'error'
  const [adMessage, setAdMessage] = useState('');
  const [adVideoUrl, setAdVideoUrl] = useState(null);
  const [videoPlaying, setVideoPlaying] = useState(false);

  const fetchStatus = async () => {
    try {
      const accessToken = JSON.parse(localStorage.getItem('accessToken') || '{}').token || '';
      if (!accessToken || !gymcode) return;

      const res = await fetch(`${API_BASE_URL}/api/shield/status?gymcode=${gymcode}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          setStatus(data);
        }
      }
    } catch (err) {
      console.error('Error fetching shield status:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gymcode]);

  const handleStartAd = async () => {
    setActionLoading(true);
    setError(null);
    try {
      const accessToken = JSON.parse(localStorage.getItem('accessToken') || '{}').token || '';
      const res = await fetch(`${API_BASE_URL}/api/shield/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ gymcode }),
      });

      const data = await res.json();
      if (res.ok && data.success) {
        setAdSessionId(data.shieldSessionId);
        setAdVideoUrl(data.videoUrl);
        setVideoPlaying(false);
        setShowAdModal(true);
        setCountdown(10);
        setAdCompleteStep('watching');
      } else {
        setError(data.message || 'Failed to start shield session.');
      }
    } catch (err) {
      console.error(err);
      setError('A network error occurred. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  // Timer effect for ad simulation / video watch duration
  useEffect(() => {
    let timer;
    if (showAdModal && adCompleteStep === 'watching') {
      if (!adVideoUrl || videoPlaying) {
        if (countdown > 0) {
          timer = setTimeout(() => setCountdown(prev => prev - 1), 1000);
        } else {
          handleCompleteAd();
        }
      }
    }
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAdModal, countdown, adCompleteStep, adVideoUrl, videoPlaying]);

  const handleCompleteAd = async () => {
    setAdCompleteStep('claiming');
    try {
      const accessToken = JSON.parse(localStorage.getItem('accessToken') || '{}').token || '';
      const res = await fetch(`${API_BASE_URL}/api/shield/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ shieldSessionId: adSessionId }),
      });

      const data = await res.json();
      if (res.ok && data.success) {
        setAdCompleteStep('success');
        fetchStatus();
        if (onShieldActivated) {
          onShieldActivated();
        }
        // Auto close after 2 seconds
        setTimeout(() => {
          setShowAdModal(false);
        }, 2000);
      } else {
        setAdCompleteStep('error');
        setAdMessage(data.message || 'Failed to verify ad completion.');
      }
    } catch (err) {
      console.error(err);
      setAdCompleteStep('error');
      setAdMessage('Network error verification failed.');
    }
  };

  if (loading || !status) {
    return (
      <div className="bg-[#181818]/60 border border-[#2b2b2b] rounded-xl p-4 flex items-center justify-between animate-pulse mb-6">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded bg-[#222]" />
          <div className="space-y-2">
            <div className="h-4 bg-[#222] rounded w-24" />
            <div className="h-3 bg-[#222] rounded w-48" />
          </div>
        </div>
        <div className="w-20 h-8 bg-[#222] rounded-lg" />
      </div>
    );
  }

  const {
    shields_remaining_this_month,
    shield_prompt_active,
    already_used_today,
    days_since_last_visit
  } = status;

  // Determine helper message for inactive states
  let helperText = '';
  if (!shield_prompt_active) {
    if (already_used_today) {
      helperText = 'You already used a shield today.';
    } else if (shields_remaining_this_month === 0) {
      helperText = 'No shields remaining for this month (4/4 used).';
    } else if (days_since_last_visit < 2) {
      helperText = 'Streak is safe! No shield needed today.';
    } else if (days_since_last_visit >= 3) {
      helperText = 'Streak is broken and cannot be recovered by one shield.';
    } else {
      helperText = 'Streak Shield is currently unavailable.';
    }
  }

  return (
    <div className="mb-6 relative w-full">
      {/* Streak Shield Card */}
      <div
        className={`bg-gradient-to-br from-[#121212] to-[#161616] rounded-xl p-5 border transition-all duration-300 ${
          shield_prompt_active
            ? 'border-[#6CD800] shadow-lg shadow-[#6CD800]/10'
            : 'border-[#2b2b2b]/80 opacity-70'
        }`}
      >
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-start gap-4">
            <div
              className={`p-2.5 rounded-lg ${
                shield_prompt_active ? 'bg-[#6CD800]/10 text-[#6CD800]' : 'bg-gray-800/40 text-gray-500'
              }`}
            >
              <Shield size={24} className={shield_prompt_active ? 'animate-pulse' : ''} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h4 className="font-bold text-white text-base">Streak Shield</h4>
                <span className="text-[11px] font-black uppercase bg-gray-800/80 px-2 py-0.5 rounded text-gray-300">
                  {4 - shields_remaining_this_month}/4 Used
                </span>
              </div>
              <p className="text-sm text-gray-300 mt-1">
                {shield_prompt_active
                  ? 'Watch a 10s ad to protect yesterday\'s missed check-in and keep your streak alive!'
                  : helperText}
              </p>
            </div>
          </div>

          <div className="w-full sm:w-auto flex flex-col items-stretch sm:items-end gap-1">
            <button
              onClick={handleStartAd}
              disabled={!shield_prompt_active || actionLoading}
              className={`px-5 py-2.5 rounded-lg font-black text-xs uppercase tracking-wider transition-all duration-200 flex items-center justify-center gap-1.5 ${
                shield_prompt_active
                  ? 'bg-[#6CD800] hover:bg-[#5bc200] text-[#0B0F19] hover:shadow-md cursor-pointer'
                  : 'bg-gray-800/40 text-gray-500 border border-gray-700/30 cursor-not-allowed'
              }`}
            >
              {actionLoading ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <>
                  <Play size={12} fill="currentColor" />
                  Watch Ad
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="mt-2 text-xs text-red-400 bg-red-950/20 border border-red-900/30 px-3 py-2 rounded-lg flex items-center gap-1.5">
          <AlertTriangle size={12} />
          <span>{error}</span>
        </div>
      )}

      {/* Simulated Ad Modal */}
      {showAdModal && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-md z-50 flex items-center justify-center p-4">
          <div className="bg-[#121212] border border-[#2b2b2b] rounded-2xl w-full max-w-lg overflow-hidden shadow-2xl relative">
            
            {/* Ad Header */}
            <div className="px-5 py-4 border-b border-[#2b2b2b] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Tv size={18} className="text-[#6CD800]" />
                <span className="text-xs uppercase tracking-wider text-gray-400 font-bold">Sponsored Ad</span>
              </div>
              <div className="flex items-center gap-3">
                {adCompleteStep === 'watching' && (
                  <div className="bg-[#6CD800]/10 border border-[#6CD800]/30 text-[#6CD800] text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                    <Loader2 size={12} className="animate-spin" />
                    <span>0:0{countdown}</span>
                  </div>
                )}
                {/* Close button - disabled unless error or complete */}
                <button
                  disabled={adCompleteStep === 'watching' || adCompleteStep === 'claiming'}
                  onClick={() => setShowAdModal(false)}
                  className={`p-1.5 rounded-lg transition-colors ${
                    adCompleteStep === 'watching' || adCompleteStep === 'claiming'
                      ? 'text-gray-700 cursor-not-allowed'
                      : 'text-gray-400 hover:text-white hover:bg-gray-800'
                  }`}
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Ad Content Box */}
            <div className="p-8 flex flex-col items-center justify-center min-h-[280px] text-center relative overflow-hidden bg-gradient-to-br from-[#101010] to-[#181818]">
              
              {/* Grid Background */}
              <div className="absolute inset-0 bg-[linear-gradient(to_right,#222_1px,transparent_1px),linear-gradient(to_bottom,#222_1px,transparent_1px)] bg-[size:24px_24px] opacity-10 pointer-events-none" />

              {adCompleteStep === 'watching' && adVideoUrl ? (
                <div className="relative w-full h-full min-h-[220px] flex items-center justify-center bg-black rounded-lg overflow-hidden z-10">
                  <video
                    src={adVideoUrl}
                    autoPlay
                    muted
                    playsInline
                    className="w-full max-h-[220px] object-contain"
                    onPlay={() => setVideoPlaying(true)}
                    onError={() => {
                      console.error("Failed to load video ad, falling back to simulated ad.");
                      setAdVideoUrl(null);
                    }}
                  />
                </div>
              ) : adCompleteStep === 'watching' && (
                <div className="space-y-6 relative z-10 animate-fade-in">
                  <div className="w-20 h-20 mx-auto bg-[#6CD800]/10 border-2 border-[#6CD800] rounded-full flex items-center justify-center shadow-lg shadow-[#6CD800]/5">
                    <Shield size={38} className="text-[#6CD800] animate-pulse" />
                  </div>
                  <div className="space-y-2 max-w-sm">
                    <h3 className="text-xl font-extrabold text-white">FitScan Pro</h3>
                    <p className="text-sm text-gray-400 leading-relaxed">
                      Upgrade to FitScan Pro to unlock real-time workout tracking, advanced analytics, and custom routine builders.
                    </p>
                  </div>
                  <div className="flex justify-center gap-2.5">
                    <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-1 bg-gray-800 rounded text-gray-400 border border-gray-700/50">
                      Real-time Stats
                    </span>
                    <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-1 bg-gray-800 rounded text-gray-400 border border-gray-700/50">
                      Unlimited Milestones
                    </span>
                    <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-1 bg-gray-800 rounded text-gray-400 border border-gray-700/50">
                      Ad-free Experience
                    </span>
                  </div>
                </div>
              )}

              {adCompleteStep === 'claiming' && (
                <div className="space-y-4 relative z-10">
                  <Loader2 size={40} className="text-[#6CD800] animate-spin mx-auto" />
                  <div>
                    <h3 className="text-lg font-bold text-white">Saving Streak...</h3>
                    <p className="text-xs text-gray-400 mt-1">Completing check-in backdating.</p>
                  </div>
                </div>
              )}

              {adCompleteStep === 'success' && (
                <div className="space-y-4 relative z-10 animate-scale-up">
                  <div className="w-20 h-20 mx-auto bg-green-500/10 border-2 border-green-500 rounded-full flex items-center justify-center shadow-lg shadow-green-500/10">
                    <CheckCircle2 size={38} className="text-green-500 animate-bounce" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-black text-white">Streak Secured! 🛡️</h3>
                    <p className="text-sm text-green-400 font-medium mt-1">Missed check-in backdated to yesterday.</p>
                  </div>
                </div>
              )}

              {adCompleteStep === 'error' && (
                <div className="space-y-4 relative z-10">
                  <div className="w-16 h-16 mx-auto bg-red-500/10 border-2 border-red-500 rounded-full flex items-center justify-center">
                    <AlertTriangle size={30} className="text-red-500" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">Failed to Claim Shield</h3>
                    <p className="text-sm text-red-400 mt-1">{adMessage}</p>
                  </div>
                  <button
                    onClick={() => setShowAdModal(false)}
                    className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg text-xs font-semibold uppercase tracking-wider"
                  >
                    Close Modal
                  </button>
                </div>
              )}
            </div>

            {/* Ad Footer */}
            <div className="px-5 py-3 bg-[#0c0c0c] border-t border-[#2b2b2b] flex items-center justify-between text-[11px] text-gray-500">
              <span>FitScan Advertising Network</span>
              <span>10s Shield Validation</span>
            </div>

          </div>
        </div>
      )}
    </div>
  );
};

export default StreakShield;
